import { isEmpty } from 'lodash';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import { getStrategyOptions, Strategy } from '#/shared/strategies';
import { getActiveProducts } from '#/products/domain';
import { Product } from '#/products/types';
import { StoredCp } from '#/unified-rfqs/services/counterparties';
import { getSelectableVenues, Source } from '../../entities/products/venue';
import { FormOption } from '../rfq-builder/types';

export type Field =
  | 'venues'
  | 'instruments'
  | 'assets'
  | 'auctionTypes'
  | 'products'
  | 'strategies'
  | 'counterparties';

interface SelectView<T> {
  readonly options: FormOption<T>[];
  selectedOptions: FormOption<T>[];
}

export interface FormView {
  readonly productCodes: string[];
  readonly venues: SelectView<Product['venue']>;
  readonly instruments: SelectView<Product['kind']>;
  readonly products: SelectView<Product['shortName']>;
  readonly assets: SelectView<Product['currency']>;
  readonly auctionTypes: SelectView<AuctionType>;
  readonly strategies: SelectView<Strategy['shortCode']>;
  readonly counterparties: SelectView<string>;
  readonly notional: {
    readonly min: string;
    readonly max: string;
  };
}

export interface PersistedSelectedFilters {
  readonly productCodes: string[] | undefined;
  readonly auctionTypes:
    | FormView['auctionTypes']['selectedOptions']
    | undefined;
  readonly assets: FormView['assets']['selectedOptions'] | undefined;
  readonly instruments: FormView['instruments']['selectedOptions'] | undefined;
  readonly venues: FormView['venues']['selectedOptions'] | undefined;
  readonly products: FormView['products']['selectedOptions'] | undefined;
  readonly strategies: FormView['strategies']['selectedOptions'] | undefined;
  readonly notional: FormView['notional'] | undefined;
  readonly counterparties:
    | FormView['counterparties']['selectedOptions']
    | undefined;
}

export const getVenueOptions = () => {
  return getSelectableVenues().map((venue) => ({
    label: venue.shortName,
    value: venue.code,
  }));
};

export const getInstrumentOptions = (): FormOption<'FUTURE' | 'OPTION'>[] => {
  return [
    { value: 'FUTURE', label: 'Future' },
    { value: 'OPTION', label: 'Option' },
  ];
};

const getAuctionTypeOptions = (): FormOption<AuctionType>[] => {
  return [
    { value: 'OB', label: 'Order Book' },
    { value: 'RFQ', label: 'RFQ' },
  ];
};

const getCounterpartyOptions = (
  counterparties: readonly StoredCp[] = [],
): FormOption<string>[] => {
  return counterparties.map((cp) => ({
    value: cp.data.desk_name,
    label: cp.data.desk_name,
  }));
};

const activeProducts = getActiveProducts();

export const getVenueAssetOptions = (products = activeProducts) => {
  const assetsSet = new Set(
    Object.values(products).map((product) => product.currency),
  );

  return [...assetsSet].map((asset) => ({
    value: asset,
    label: asset,
  }));
};

export const getProductOptions = (products = activeProducts) => {
  const productSet = new Set(
    Object.values(products).map((product) => ({
      shortName: product.shortName,
      code: product.code,
    })),
  );

  return [...productSet].map((product) => ({
    value: product.code,
    label: product.shortName,
  }));
};

export const INITIAL_STATE: FormView = {
  productCodes: [],
  venues: {
    options: getVenueOptions(),
    selectedOptions: [],
  },
  assets: {
    options: getVenueAssetOptions(),
    selectedOptions: [],
  },
  products: {
    options: getProductOptions(),
    selectedOptions: [],
  },
  instruments: {
    options: getInstrumentOptions(),
    selectedOptions: [],
  },
  auctionTypes: {
    options: getAuctionTypeOptions(),
    selectedOptions: [],
  },
  strategies: {
    options: getStrategyOptions(true),
    selectedOptions: [],
  },
  counterparties: {
    options: getCounterpartyOptions(),
    selectedOptions: [],
  },
  notional: {
    min: '',
    max: '',
  },
};

function getOptionValues<T extends FormOption<string>>(
  options: readonly T[],
): T['value'][] {
  return options.map((option) => option.value);
}

export function getFormFilteredProducts(state: FormView) {
  return getFilteredProducts({
    assets: state.assets.selectedOptions,
    instruments: state.instruments.selectedOptions,
    venues: state.venues.selectedOptions,
    products: state.products.selectedOptions,
  });
}

interface FilteredProductsArgs {
  readonly assets: FormOption<string>[];
  readonly instruments: FormOption<'FUTURE' | 'OPTION'>[];
  readonly venues: FormOption<Source>[];
  readonly products: FormOption<string>[];
}

export function getFilteredProducts(options: FilteredProductsArgs | null) {
  if (options == null) return [];

  const assets = getOptionValues(options.assets);
  const instruments = getOptionValues(options.instruments);
  const venues = getOptionValues(options.venues);
  const productCodes = getOptionValues(options.products);

  if (!isEmpty(productCodes)) {
    return activeProducts.filter((product) =>
      productCodes.includes(product.code),
    );
  }

  return activeProducts.filter(
    (product) =>
      (isEmpty(assets) || assets.includes(product.currency)) &&
      (isEmpty(instruments) || instruments.includes(product.kind)) &&
      (isEmpty(venues) || venues.includes(product.venue)),
  );
}
